import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { useFeedItems, useFeedSources } from 'src/api/queries';
import { AbsenceState } from 'src/components/editorial/AbsenceState';
import { LoadFailure } from 'src/components/editorial/LoadFailure';
import { PageHeader } from 'src/components/editorial/PageHeader';
import { Button } from 'src/components/ui/Button';
import { Skeleton } from 'src/components/ui/Skeleton';
import { useDebouncedValue } from 'src/lib/useDebouncedValue';
import { formatDateTime } from 'src/lib/time';
import type { EditorialFeedItem } from 'src/types/editorial';

const ROWS_PER_PAGE = 25;

/**
 * Everything the subscriptions have brought in, newest first.
 *
 * A reading list, not a work queue: nothing here has a state, an owner or a next stage, because
 * nothing downstream runs on it. What an editor does with a post is open the publisher's page.
 *
 * The source filter lives in the query string so a link from Suscripciones lands already filtered
 * and stays shareable.
 */
export function FeedItemsWorkspace() {
  const [searchParams, setSearchParams] = useSearchParams();
  const sourceId = searchParams.get('fuente') ?? '';
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  // The list refetches per keystroke otherwise, and the server answers a regex query each time.
  const debouncedSearch = useDebouncedValue(search, 300);

  const sources = useFeedSources();
  const items = useFeedItems({ page, rowsPerPage: ROWS_PER_PAGE, sourceId: sourceId || undefined, search: debouncedSearch || undefined });

  function changeSource(nextSourceId: string) {
    setPage(1);
    setSearchParams(nextSourceId ? { fuente: nextSourceId } : {}, { replace: true });
  }

  return (
    <div className="page-wide">
      <PageHeader title="Publicaciones">
        Lo que han publicado los sitios suscritos. Guardamos una copia de cada entrada —título,
        resumen y liga— tal como llegó; la nota completa sigue siendo del medio que la publicó.
      </PageHeader>

      <div className="posts__filters">
        <label>
          <span className="visually-hidden">Filtrar por fuente</span>
          <select
            value={sourceId}
            onChange={event => changeSource(event.target.value)}
            disabled={sources.isPending}
            aria-label="Filtrar por fuente"
          >
            <option value="">Todas las fuentes</option>
            {(sources.data?.sources ?? []).map(source => (
              <option key={source.sourceId} value={source.sourceId}>
                {source.name}
              </option>
            ))}
          </select>
        </label>

        <label>
          <span className="visually-hidden">Buscar por título</span>
          <input
            type="search"
            value={search}
            onChange={event => {
              setPage(1);
              setSearch(event.target.value);
            }}
            placeholder="Buscar en los títulos"
          />
        </label>

        <Link className="posts__manage" to="/suscripciones">
          Administrar suscripciones
        </Link>
      </div>

      {items.isPending && <Skeleton variant="rows" count={8} />}
      {items.isError && <LoadFailure title="No se pudieron cargar las publicaciones" error={items.error} onRetry={items.refetch} />}
      {items.data && items.data.items.length === 0 && (
        <AbsenceState kind="empty" what={debouncedSearch || sourceId ? 'Ninguna publicación coincide con el filtro.' : 'Todavía no se ha copiado ninguna publicación.'} />
      )}

      {items.data && items.data.items.length > 0 && (
        <ul className="posts">
          {items.data.items.map(item => (
            <PostRow key={item.itemId} item={item} />
          ))}
        </ul>
      )}

      {items.data && items.data.totalRecords > 0 && (
        <div className="posts__pager">
          <Button variant="ghost" disabled={page === 1} onClick={() => setPage(current => Math.max(current - 1, 1))}>
            Anterior
          </Button>
          <span className="relevance__note mono">
            {page} de {Math.max(items.data.totalPages, 1)} · {items.data.totalRecords} publicaciones
          </span>
          <Button variant="ghost" disabled={!items.data.hasNextPage} onClick={() => setPage(current => current + 1)}>
            Siguiente
          </Button>
        </div>
      )}
    </div>
  );
}

function PostRow({ item }: { item: EditorialFeedItem }) {
  return (
    <li className="post">
      <div className="post__head">
        <span className="post__source">{item.sourceName}</span>
        <time className="post__time mono" dateTime={item.publishedAt}>
          {formatDateTime(item.publishedAt)}
        </time>
      </div>

      <h3 className="post__title">
        {/* The copy is a pointer, deliberately: the article belongs to the publisher and reading
            it happens on their page. */}
        <a href={item.link} target="_blank" rel="noreferrer noopener">
          {item.title || 'Sin título'}
        </a>
      </h3>

      {item.summary && <p className="post__summary">{item.summary}</p>}

      <div className="post__meta">
        {item.author && <span>{item.author}</span>}
        {item.categories.slice(0, 4).map(category => (
          <span key={category} className="post__tag">
            {category}
          </span>
        ))}
      </div>
    </li>
  );
}
